import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Technical Support";
const heroImage = "https://net-basis.net/Bilder/technical-support-dach-customer-service.webp";
const heroAlt = "Hand with screwdriver uses a digital interface for technical support in the DACH market.";
const heroClaimLine1 = "Customer service";
const heroClaimLine2 = "with advice on site!";
const benefitsTitle = "How you benefit from our __Technical Support__ expertise:";
const h2Intro = "NetBasis International offers extensive local technical support upon request, enabling our manufacturer partners to guarantee high availability of technical customer service on site. This ensures that customers benefit immediately and comprehensively from the value and utility of the products and solutions we represent.";
const p1Text = "Our highly specialized service helps customers and partners deploy and utilize products and solutions quickly and easily, as well as resolve any issues. At the same time, the direct connection of NetBasis International to our manufacturer partners ensures a consistently high quality of support throughout the entire DACH region.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Answering general questions regarding the application and utilization of products and solutions",
  "Expert assistance with and regarding specific operational information",
  "Reliable provision of installation and configuration manuals",
  "Professional on-site installation and setup assistance",
  "Fast local support via telephone, email, and the internet",
  "Precise error analysis and troubleshooting via modern remote maintenance",
  "Identification and verification of any errors and their underlying root causes",
  "Development of concrete resolution proposals for identified errors and malfunctions",
  "Professional maintenance in accordance with corresponding arrangements and Service Level Agreements",
  "Structured multi-tier and multi-level support for complex ICT systems",
];

export default function TechnicalSupportEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Technical Support | NetBasis International"
        description="Reliable local first and second level support to ensure high customer satisfaction in the DACH region."
        ogUrl="https://net-basis.net/en/services/business-development/technical-support"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center pt-[10%] md:pt-[8%] lg:pt-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[technischerSupportTextSlide_12s_ease-in-out_infinite]"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>{" "}
                  <span className="block md:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 leading-relaxed text-sm md:text-base mb-4">
              {h2Intro}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>{p1Text}</p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}